import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { FiArrowRight, FiPlay } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";

const Hero = ({
  onAuthClick,
  benchmarkSetCount,
  subjectCount,
  loadingStats,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleStartBenchmarking = () => {
    if (user) {
      navigate("/run");
    } else {
      onAuthClick("signin", "/run");
    }
  };

  return (
    <section className="relative min-h-[90vh] overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Gradient background blurs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Large primary orb - top left */}
        <div className="absolute -left-32 -top-32 size-[500px] rounded-full bg-gradient-to-br from-primary-400/40 to-primary-600/20 blur-3xl" />

        {/* Cyan orb - top right */}
        <div className="absolute -right-20 top-20 size-[400px] rounded-full bg-gradient-to-bl from-cyan-300/50 to-blue-400/30 blur-3xl" />

        {/* Purple accent - center */}
        <div className="absolute left-1/2 top-1/2 size-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-violet-200/30 via-primary-200/40 to-cyan-200/30 blur-3xl" />

        {/* Bottom accent */}
        <div className="absolute -bottom-40 left-1/4 h-[400px] w-[500px] rounded-full bg-gradient-to-t from-blue-300/30 to-primary-200/20 blur-3xl" />

        {/* Small accent orb */}
        <div className="absolute right-1/4 top-1/3 size-64 rounded-full bg-cyan-200/40 blur-2xl" />
      </div>

      <div className="relative mx-auto flex min-h-[90vh] max-w-6xl flex-col items-center justify-center px-4 py-20 sm:px-6 lg:px-8">
        {/* Main Heading */}
        <h1 className="mb-6 text-center">
          <span className="block bg-gradient-to-r from-primary-700 via-primary-500 to-cyan-500 bg-clip-text text-5xl font-bold tracking-tight text-transparent sm:text-6xl lg:text-7xl">
            Incept Benchmark
          </span>
        </h1>

        {/* Subtitle */}
        <p className="mx-auto mb-10 max-w-2xl text-center text-lg leading-relaxed text-gray-500 sm:text-xl">
          Evaluate your educational content against InceptBench standards.
          Compare results, track performance, and ship quality content.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <button
            onClick={handleStartBenchmarking}
            className="group inline-flex items-center gap-2 rounded-full bg-gray-900 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-xl"
          >
            <FiPlay className="size-4" />
            Start Benchmarking
            <FiArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <Link
            to="/benchmark-sets"
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-8 py-4 text-base font-semibold text-gray-700 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md"
          >
            Explore Datasets
          </Link>
        </div>

        {/* Stats Row */}
        <div className="mt-16 flex items-center gap-8 sm:gap-16">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
              {loadingStats ? "—" : benchmarkSetCount}
            </div>
            <div className="mt-1 text-sm text-gray-500">Benchmark Sets</div>
          </div>
          <div className="h-10 w-px bg-gray-400" />
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
              K-12
            </div>
            <div className="mt-1 text-sm text-gray-500">Grade Coverage</div>
          </div>
          <div className="h-10 w-px bg-gray-400" />
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 sm:text-4xl">
              {loadingStats ? "—" : subjectCount}
            </div>
            <div className="mt-1 text-sm text-gray-500">Subjects</div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};

Hero.propTypes = {
  onAuthClick: PropTypes.func.isRequired,
  benchmarkSetCount: PropTypes.number.isRequired,
  subjectCount: PropTypes.number.isRequired,
  loadingStats: PropTypes.bool.isRequired,
};

export default Hero;
